import { Link, Switch } from '@mui/material';
import { useState } from 'react';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  PartnerGroupRules,
  PartnerSetting,
  useGroupRules,
  useManagePartnerSettingsValue,
  usePartnerSettings,
  useSelectedPartner,
} from 'src/graphql';
import { useSmsSessionGroupOptions } from 'src/pages/Settings/preferences/hooks/use-sms-session-group-options';
import {
  findPartnerSettingRecord,
  SettingRecord,
  useSmsSessionPolicy,
} from 'src/pages/Settings/preferences/hooks/use-sms-session-policy';
import { useGlobalStore } from 'src/store';
import { isStringBooleanTrue } from 'src/utils/strings';
import { SmsSessionTimerFields } from './sms-session-timer-fields';

const SMS_SETTINGS = [PartnerSetting.SmsSessionsOn, PartnerSetting.SmsSessionPolicyByGroup];

/** Whether the partner runs the group on the platform's entry or on its own. */
const GroupPolicyStatus = ({
  customized,
  partnerName,
  onReset,
}: {
  customized: boolean;
  partnerName: string | undefined;
  onReset: () => void;
}) => {
  if (!customized) {
    return <>Using the platform default</>;
  }

  return (
    <>
      Customized for {partnerName ?? 'this partner'}.{' '}
      <Link
        component="button"
        type="button"
        variant="body2"
        fontWeight={600}
        onClick={onReset}
        sx={{ verticalAlign: 'baseline' }}
      >
        Reset to platform default
      </Link>
    </>
  );
};

interface SmsSessionPreferencesContentProps {
  records: SettingRecord[];
  partnerName: string | undefined;
  groupOptions: SelectMenuOption[];
  defaultGroupKey: string | null;
}

const SmsSessionPreferencesContent = ({
  records,
  partnerName,
  groupOptions,
  defaultGroupKey,
}: SmsSessionPreferencesContentProps) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { updatePartnerSettingsValue, createPartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);
  const policy = useSmsSessionPolicy(records, partnerId);

  const enabledRecord = findPartnerSettingRecord(
    records,
    partnerId,
    PartnerSetting.SmsSessionsOn,
  );
  const enabled = isStringBooleanTrue(
    enabledRecord?.value ??
      findPartnerSettingRecord(records, PLATFORM_PARTNER_ID, PartnerSetting.SmsSessionsOn)
        ?.value,
  );

  const [isSavingEnabled, setIsSavingEnabled] = useState(false);
  const [groupKey, setGroupKey] = useState(defaultGroupKey);
  // Bumped per group by a reset, so only that group's fields start over from the entry it falls back to.
  const [resetCounts, setResetCounts] = useState<Record<string, number>>({});
  const remountGroup = (key: string): void =>
    setResetCounts((counts) => ({ ...counts, [key]: (counts[key] ?? 0) + 1 }));

  const handleEnabledToggle = async (checked: boolean): Promise<void> => {
    setIsSavingEnabled(true);
    try {
      const value = String(checked);
      if (enabledRecord?.partnerSettingValueId) {
        await updatePartnerSettingsValue({
          partnerSettingValueId: enabledRecord.partnerSettingValueId,
          value,
          partnerSettingId: PartnerSetting.SmsSessionsOn,
        });
      } else {
        await createPartnerSettingsValue({
          value,
          partnerSettingId: PartnerSetting.SmsSessionsOn,
        });
      }
    } finally {
      setIsSavingEnabled(false);
    }
  };

  const handleReset = async (key: string): Promise<void> => {
    const reset = policy.resetEntry(key);
    // Remount on the entry the group falls back to; a failed reset puts the stored entry back.
    remountGroup(key);
    if (!(await reset)) {
      remountGroup(key);
    }
  };

  return (
    <SettingsSection
      title="SMS sessions"
      description="Configure live SMS sessions inside conversations."
    >
      <SettingsSectionRow
        title="Enable SMS sessions"
        secondary="Allow agents to start live SMS sessions from conversation threads."
        control={
          <Switch
            checked={enabled}
            disabled={isSavingEnabled}
            onChange={(e) => void handleEnabledToggle(e.target.checked)}
            inputProps={{ 'aria-label': 'Enable SMS sessions' }}
          />
        }
      />
      <SettingsSectionRow
        title="Assignee group"
        secondary={
          groupKey && partnerId !== PLATFORM_PARTNER_ID ? (
            <GroupPolicyStatus
              customized={policy.isCustomized(groupKey)}
              partnerName={partnerName}
              onReset={() => void handleReset(groupKey)}
            />
          ) : undefined
        }
        control={
          <SelectMenu
            label="Assignee group"
            placeholder="No assignee groups"
            options={groupOptions}
            value={groupKey}
            onChange={setGroupKey}
            disabled={!groupOptions.length}
            sectioned
            sx={{ minWidth: '13.75rem' }}
          />
        }
      />
      {groupKey ? (
        <SmsSessionTimerFields
          key={`${groupKey}:${resetCounts[groupKey] ?? 0}`}
          entry={policy.entryFor(groupKey)}
          onSave={(patch) => policy.saveEntry(groupKey, patch)}
        />
      ) : null}
    </SettingsSection>
  );
};

/** Timers are set per assignee group and read on every fire, so a change reaches open sessions. */
export const SmsSessionPreferences = () => {
  const { checkValidity } = useGroupRules();
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const partnerName = useSelectedPartner().selectedPartner?.businessName;
  const { data, loading } = usePartnerSettings({ partnerId, partnerSetting: SMS_SETTINGS });
  const groups = useSmsSessionGroupOptions(partnerId, partnerName);

  if (!checkValidity([PartnerGroupRules.SmsSessionsAvailable]) || loading || groups.loading) {
    return null;
  }

  // Remount on partner switch so the field state re-initializes from that partner's records.
  return (
    <SmsSessionPreferencesContent
      key={partnerId}
      records={data}
      partnerName={partnerName}
      groupOptions={groups.options}
      defaultGroupKey={groups.defaultGroupKey}
    />
  );
};
